import { useQuery } from '@tanstack/react-query';
import { getIngestStatus, qk, type IngestStatusResponse } from '../api.js';

/**
 * §13 — KURULUM DURUMU: "SDK gerçekten kurulu mu?" sorusunun tek cevabı.
 *
 * NEDEN VAR — panelin en pahalı yanlış anlaşılması buydu: bütün sayfalar
 * `daily_agg`'den okur, o da `dxp.sessions`'ın MV'sidir ve oraya satır YALNIZCA
 * OTURUM KAPANINCA yazılır (§11.5 — 30 dk hareketsizlik + 60 sn sweep). Yani etiketi
 * DOĞRU kuran müşteri de ilk yarım saat boyunca "Henüz veri yok — SDK'yı ekleyin"
 * görüyor, kurulumunu bozuk sanıp etiketi söküyordu. Boş durum metni doğruydu ama
 * TEŞHİS YANLIŞTI: veri yokluğu iki AYRI nedenden doğar ve ikisi farklı iş gerektirir.
 *
 * Karar bu dosyada SAF bir fonksiyondadır (`kurulumVerdict`): hem Ayarlar kartı hem
 * Genel Bakış'ın boş durumu aynı cümleyi kurar, iki ekran birbiriyle çelişemez.
 */

/** Kurulumun ayırt edilebilir durumları — her biri FARKLI bir iş gerektirir. */
export type KurulumKind =
  | 'bilinmiyor'
  | 'arsivli'
  | 'olay-yok'
  | 'sustu'
  | 'yalniz-bot'
  | 'oturum-bekleniyor'
  | 'akiyor';

export interface KurulumVerdict {
  kind: KurulumKind;
  /** Rozet metni — tek bakışta durum. */
  label: string;
  /** Kullanıcıya NE YAPACAĞINI söyleyen cümle. */
  description: string;
  /** Etiket gerçekten çalışıyor mu (olay alınıyor mu) — boş durum metnini bu belirler. */
  receiving: boolean;
}

/**
 * Saf karar — sırası ÖNEMLİDİR: en spesifik neden önce sorulur.
 *
 * `null` girdi "sunucu söylemedi" demektir (eski sürüm / uç yok / hata) ve
 * 'bilinmiyor'a düşer: teşhis edemediğimizde SUSARIZ, "kurulu değil" DEMEYİZ —
 * yanlış teşhis, teşhis yokluğundan pahalıdır.
 */
export function kurulumVerdict(status: IngestStatusResponse | null | undefined): KurulumVerdict {
  if (status === null || status === undefined) {
    return {
      kind: 'bilinmiyor',
      label: 'durum bilinmiyor',
      description:
        'Kurulum durumu bu sunucu sürümünden okunamadı. Etiketi kurduysanız verinin belirmesi için bekleyin.',
      receiving: false,
    };
  }

  // §72.4 — arşivli projede /collect 403 döner: veri gelmemesi BEKLENEN davranıştır.
  if (status.archived) {
    return {
      kind: 'arsivli',
      label: 'arşivli — veri alınmıyor',
      description:
        'Proje arşivli olduğu için gelen olaylar reddediliyor. Veri toplamak için projeyi arşivden çıkarın.',
      receiving: false,
    };
  }

  if (status.lastEventAt === null) {
    return {
      kind: 'olay-yok',
      label: 'olay alınmadı',
      description:
        'Bu projeye son 30 günde tek bir olay ulaşmadı. Etiketin <head> içinde olduğunu ve ' +
        'data-pid değerinin bu projenin kimliğiyle birebir aynı olduğunu doğrulayın — ' +
        'başka bir pid taşıyan istek sessizce atılır.',
      receiving: false,
    };
  }

  if (status.events24h === 0) {
    return {
      kind: 'sustu',
      label: 'akış durdu',
      description:
        'Olaylar bir zamanlar geldi ama son 24 saatte hiç ulaşmadı. Etiket sayfadan kaldırılmış ' +
        'ya da site trafiği durmuş olabilir.',
      receiving: false,
    };
  }

  // §6.2 — bot trafiği agregatlara GİRMEZ; hepsi bot ise panel boş kalır ve bu, "veri yok"tan farklıdır.
  if (status.botEvents24h >= status.events24h) {
    return {
      kind: 'yalniz-bot',
      label: 'yalnız bot trafiği',
      description:
        'Son 24 saatte gelen olayların tamamı bot filtresine takıldı (§6.2) ve raporlara girmiyor. ' +
        'Testi gerçek bir tarayıcıda tekrarlayın — headless tarayıcı bot sayılır.',
      receiving: true,
    };
  }

  if (status.lastSessionAt === null) {
    return {
      kind: 'oturum-bekleniyor',
      label: 'kurulu — ilk oturum bekleniyor',
      description:
        `Olaylar alınıyor. Raporlar oturum KAPANDIĞINDA dolar: ${status.sessionGapMinutes} dakika ` +
        'hareketsizlikten sonra oturum kapanır ve tablolara yazılır. Sekmeyi kapatıp bu süreyi bekleyin.',
      receiving: true,
    };
  }

  return {
    kind: 'akiyor',
    label: 'veri akıyor',
    description: 'Olaylar alınıyor ve oturumlar kapanıp raporlara işleniyor.',
    receiving: true,
  };
}

/** Rozet tonu — `Badge` bileşeninin ton kümesiyle aynı yazım. */
export function kurulumTone(kind: KurulumKind): 'success' | 'warning' | 'danger' | 'neutral' {
  if (kind === 'akiyor') return 'success';
  if (kind === 'oturum-bekleniyor' || kind === 'yalniz-bot') return 'warning';
  if (kind === 'olay-yok' || kind === 'sustu' || kind === 'arsivli') return 'danger';
  return 'neutral';
}

/**
 * Kurulum durumu sorgusu — Ayarlar kartı ve Genel Bakış boş durumu PAYLAŞIR.
 *
 * `retry: false` + 404 yutma: uç §27.4 tablosunda yoktur, auth'u/ucu olmayan bir
 * sunucu 404 döner ve bu bir HATA DEĞİL "bilinmiyor"dur (üye listesindeki desenin
 * aynısı). `refetchInterval`: kurulumu yeni yapan kullanıcı sayfayı elle
 * yenilemeden ilk olayı görmelidir — 15 sn, yalnız pencere ÖNDEYKEN.
 */
export function useIngestStatus(
  pid: string,
  options: { enabled?: boolean } = {},
): {
  status: IngestStatusResponse | null;
  loading: boolean;
  verdict: KurulumVerdict;
} {
  const enabled = options.enabled ?? true;
  const query = useQuery({
    queryKey: qk.ingestStatus(pid),
    queryFn: ({ signal }) => getIngestStatus(pid, signal),
    enabled,
    retry: false,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });

  const bilinmiyor = query.isError || query.data === undefined;
  const status = bilinmiyor ? null : query.data;

  return {
    status: status ?? null,
    loading: query.isLoading,
    verdict: kurulumVerdict(status),
  };
}
